import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { serviceCatalog } from '@/mocks/site';
import DatePicker from '@/components/base/DatePicker';

const SUBMIT_URL = 'https://readdy.ai/api/form/db4r32hrqll58fj4oaf0';
type Status = 'idle' | 'submitting' | 'success' | 'error';
type Errors = Partial<Record<'name' | 'phone' | 'vehicle' | 'service' | 'date' | 'message', string>>;

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const inputCls = (err?: string) => `w-full h-12 px-4 rounded-md bg-background-50 border text-sm text-foreground-950 placeholder:text-foreground-500 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 ${err ? 'border-primary-400' : 'border-background-300'}`;

const lineCls = (err?: string) => `w-full h-[54px] px-0 rounded-none bg-transparent border-0 border-b text-[15px] text-foreground-950 placeholder:text-foreground-500 outline-none transition-colors duration-200 motion-reduce:transition-none focus:border-primary-500 disabled:opacity-50 ${err ? 'border-primary-400' : 'border-background-300 hover:border-foreground-600'}`;

type Props = { variant?: 'card' | 'linear' };
const INSPECTION_VALUE = 'Not sure - needs inspection';

export default function BookingForm({ variant = 'card' }: Props) {
  const linear = variant === 'linear';
  const fieldCls = linear ? lineCls : inputCls;
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [msgLen, setMsgLen] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const minDate = todayStr();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('service') ?? '';
  const preset = requested === 'inspection'
    ? INSPECTION_VALUE
    : serviceCatalog.find((s) => s.id === requested)?.title ?? '';

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const get = (k: string) => String(fd.get(k) ?? '').trim();

    if (get('company_alt')) {
      setStatus('success');
      return;
    }

    const next: Errors = {};
    if (get('name').length < 2) next.name = 'Please enter your name.';
    if (!/^[+()\d\s-]{7,20}$/.test(get('phone'))) next.phone = 'Please enter a valid phone number.';
    if (!get('vehicle')) next.vehicle = 'Tell us the make, model and year.';
    if (!get('service')) next.service = 'Please choose a service.';
    if (!get('date')) next.date = 'Please pick a preferred date.';
    else if (get('date') < minDate) next.date = 'Date cannot be in the past.';
    if (get('message').length > 500) next.message = 'Message must be 500 characters or fewer.';
    setErrors(next);
    if (Object.keys(next).length) return;

    const params = new URLSearchParams();
    fd.forEach((v, k) => {
      if (k !== 'company_alt') params.append(k, String(v));
    });

    setStatus('submitting');
    setFormError('');
    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(serverMsg || '');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setMsgLen(0);
        setResetKey((k) => k + 1);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again or call us.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className={linear ? 'border-t border-background-300 py-10' : 'bg-background-100 border border-background-300/70 rounded-lg p-8 md:p-10 text-center'} role="status">
        <div className="w-14 h-14 flex mx-auto items-center justify-center rounded-full bg-primary-500/15 text-primary-400 text-2xl"><i className="ri-check-line" /></div>
        <h3 className="mt-5 font-heading uppercase text-[28px]">Request received</h3>
        <p className="mt-2 text-sm text-foreground-700 max-w-sm mx-auto">Thanks — our service team will call you to confirm your appointment time.</p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-6 h-11 px-5 rounded-md border border-background-300 text-sm hover:border-foreground-700 whitespace-nowrap cursor-pointer">Send another request</button>
      </div>
    );
  }

  const Err = ({ k }: { k: keyof Errors }) => (errors[k] ? <p className="mt-1.5 text-xs text-primary-400">{errors[k]}</p> : null);
  const Label = ({ htmlFor, children }: { htmlFor: string; children: string }) => (
    <label htmlFor={htmlFor} className={`block ${linear ? 'mb-0 text-[11px]' : 'mb-2 text-xs'} font-medium uppercase tracking-[0.12em] text-foreground-700 font-label`}>{children}</label>
  );

  return (
    <form id="service-booking-form" data-readdy-form noValidate onSubmit={handleSubmit} className={linear ? 'relative flex flex-col flex-1' : 'relative bg-background-100 border border-background-300/70 rounded-lg p-5 md:p-8'}>
      <input type="text" name="company_alt" className="booking-aux-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly defaultValue="" onFocus={(ev) => ev.currentTarget.removeAttribute('readonly')} />
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${linear ? 'gap-x-6 gap-y-7' : 'gap-x-5 gap-y-5'}`}>
        <div>
          <Label htmlFor="bf-name">Name</Label>
          <input id="bf-name" name="name" type="text" placeholder="Full name" className={fieldCls(errors.name)} aria-invalid={!!errors.name} />
          <Err k="name" />
        </div>
        <div>
          <Label htmlFor="bf-phone">Phone</Label>
          <input id="bf-phone" name="phone" type="tel" placeholder="(555) 000-0000" className={fieldCls(errors.phone)} aria-invalid={!!errors.phone} />
          <Err k="phone" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bf-vehicle">Vehicle</Label>
          <input id="bf-vehicle" name="vehicle" type="text" placeholder="Make, model, year" className={fieldCls(errors.vehicle)} aria-invalid={!!errors.vehicle} />
          <Err k="vehicle" />
        </div>
        <div>
          <Label htmlFor="bf-service">Service Needed</Label>
          <div className="relative">
            <select key={preset} id="bf-service" name="service" defaultValue={preset} className={`${fieldCls(errors.service)} appearance-none pr-10 cursor-pointer [&>option]:bg-background-100`} aria-invalid={!!errors.service}>
              <option value="" disabled>Select a service</option>
              {serviceCatalog.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
              <option value={INSPECTION_VALUE}>Not sure — needs inspection</option>
            </select>
            <span className={`pointer-events-none absolute ${linear ? 'right-0' : 'right-3'} top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-600`}><i className="ri-arrow-down-s-line" /></span>
          </div>
          <Err k="service" />
        </div>
        <div>
          <Label htmlFor="bf-date">Preferred Date</Label>
          <DatePicker key={resetKey} id="bf-date" name="date" min={minDate} invalid={!!errors.date} className={fieldCls(errors.date)} />
          <Err k="date" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bf-message">Message (optional)</Label>
          <textarea id="bf-message" name="message" rows={3} maxLength={500} onChange={(ev) => setMsgLen(ev.target.value.length)} placeholder="Symptoms, warning lights, noises…" className={`${fieldCls(errors.message)} h-auto py-3 resize-none ${linear ? 'min-h-[84px]' : ''}`} />
          <div className="flex justify-between"><Err k="message" /><span className="ml-auto mt-1.5 text-xs text-foreground-500">{msgLen}/500</span></div>
        </div>
      </div>
      {status === 'error' && formError && (
        <p className={linear ? 'mt-6 text-sm text-primary-300 border-l-2 border-primary-500 pl-3 py-1' : 'mt-5 text-sm text-primary-300 bg-primary-500/10 border border-primary-500/30 rounded-md px-4 py-3'} role="alert">{formError}</p>
      )}
      <div className={`flex flex-col sm:flex-row justify-between gap-4 ${linear ? 'sm:items-end mt-10 lg:mt-auto lg:pt-10' : 'sm:items-center mt-6'}`}>
        <p className={`text-xs text-foreground-600 ${linear ? 'leading-none pb-px' : ''}`}>We confirm every request by phone.</p>
        {linear ? (
          <button type="submit" disabled={status === 'submitting'} className="group self-start sm:self-auto min-h-[48px] inline-flex items-center gap-3 border-b border-foreground-600 hover:border-primary-500 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold uppercase tracking-[0.16em] text-foreground-950 whitespace-nowrap cursor-pointer transition-colors duration-200">
            {status === 'submitting' ? 'Sending…' : 'Request Service'}
            <span className="w-5 h-5 flex items-center justify-center text-primary-500 text-lg transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-0.5 group-hover:-translate-y-0.5"><i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-up-line'} /></span>
          </button>
        ) : (
        <button type="submit" disabled={status === 'submitting'} className="h-12 px-7 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors">
          {status === 'submitting' ? 'Sending…' : 'Request Booking'}
        </button>
        )}
      </div>
    </form>
  );
}